import { faChartLine, faPencil } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { createRef, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import {
  getFloorAssetsAndShapes,
  getFloorDetails,
} from "../../../../core/api/services/dashbaorad-details/floor/floor.service";
import ModalComponent from "../../../../core/components/modal/modal.component";
import TableComponent from "../../../../core/components/table/table.component";
import SkeletonComponent from "../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../shared/form-builder/form-builder.component";
import { safeAtob } from "../../../../shared/helpers/enode-decode-string";
import TableSort from "../../../../shared/helpers/table-sort";
import capitalizeFirst from "../../../../shared/helpers/upper-case";
import { FloorShape } from "../../../../shared/models/floorPlan";
import TitleComponent from "../../../../shared/title/title.component";
import {
  GetAllFloorsByBlockCodeWithFaultCount,
  setResolveStatusValue,
} from "../../../../store/slices/dashboard.slice";
import {
  acknowledgementFault,
  getFaults,
  resolveFault,
} from "../../../../store/slices/fault.slice";
import "./canvas/floorPlan.scss";
import "./canvas/floorPlanGradiant.scss";
import FloorPlanComponent from "./canvas/floorPlane.component";
import gatewayInitial from "./canvas/images/gateway-initial.png";
import photocellOffline from "./canvas/images/PhotocellSensorGrey.png";

import motion26to50 from "./canvas/images/26-to-50.png";
import motion51to75 from "./canvas/images/51-to-75.png";
import motion0to25 from "./canvas/images/less-than-26.png";
import motionMoreThan75 from "./canvas/images/more-than-75.png";

import circleLightFault from "./canvas/images/circle-light-fault.png";
import circleLightOff from "./canvas/images/circle-light-off.png";
import circleLightOn from "./canvas/images/circle-light-on.png";

import HasPermission from "src/core/guards/has-permission";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import FloorPlanHeatmapComponent from "./canvas/floorPlan.Heatmap.component";
import UpdateFloorPlanComponent from "./canvas/updateFloorPlan.component";
import DashboardFloorplanWildcardSearchingComponent from "./dashboard-floorplan-wildcard-searching.component";
import { DashboardModules } from "src/core/enums/module-name-enums";

const DashboardFloorPlanViewComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const location = useLocation();

  const { pathname } = location;
  const queryParams = new URLSearchParams(location.search);

  const query = queryParams.get("update");

  const blockCode = params.blockCode ? safeAtob(params.blockCode) : null;

  const [PageNumber, setPageNum] = useState<number>(1);
  const pageSize = 10;
  const formRef = createRef();
  const formRefFloor = createRef();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [shapes, setShapes] = useState<FloorShape[]>([]);
  const [updateJSX, setUpdateJSX] = useState(0);
  const [updateJSXHeatmap, setUpdateJSXHeatmap] = useState(0);

  const floorCode = params.floor;

  const floor = params?.floor;
  const [modalLabel, setModalLabel] = useState<any>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAcknowledgeOpen, setIsAcknowledgeOpen] = useState<boolean>(false);

  const [isAcknowledgeDisabled, setIsAcknowledgeDisabled] =
    useState<boolean>(true);
  const [isResolveOpen, setIsResolveOpen] = useState<boolean>(false);

  const [isResolveDisabled, setIsResolveDisabled] = useState<boolean>(true);

  const [initialValues, setInitialValues] = useState<any>({
    blockNumber: "",
    sensorId: "",
    faultDateTime: "",
    gateway: "",
    zone: "",
    faultDescription: "",
    remarks: "",
  });
  const [initialValuesFloor, setInitialValuesFloor] = useState<any>({
    type: "2",
  });

  const [SortBy, setSortBy] = useState<any>(capitalizeFirst("timestamp"));
  const [SortDirection, setSortDirection] = useState<any>("desc");
  const [isProcessingImage, setIsProcessingImage] = useState<boolean>(true);

  const [columns, setColumns] = useState([
    {
      title: "Asset ID",
      label: "assetCode",
      width: "17rem",
    },
    {
      title: "Status",
      label: "status",
    },
    {
      title: "Report Date & Time",
      label: "timestamp",
      type: "datetime",
      width: "16em",
      isSorted: true,
    },
    {
      title: "Fault Type",
      label: "faultType",
      width: "13rem",
    },
    {
      title: "Fault Description",
      label: "faultDescription",
      width: "13rem",
    },
    {
      title: "Acknowledged By",
      label: "acknowledgeBy",
      width: "12rem",
    },
    {
      title: "Acknowledged Time",
      label: "acknowledgeDate",
      type: "datetime",
      width: "13rem",
    },
    {
      title: "Remarks",
      label: "remarks",
    },
  ]);

  const [
    selectedSearchedAssetCodeForHighlighting,
    setSelectedSearchedAssetCodeForHighlighting,
  ] = useState(""); // hightlight the asset which is searched from the search bar

  useEffect(() => {
    return () => {
      setSelectedSearchedAssetCodeForHighlighting(""); // Reset the state
    };
  }, []);

  /*   const socketRealTimeUpdate = useSelector((state: any) => {
    return state.asset.socketRealTimeUpdate;
  });

  const socketRealTimeUpdateData = useSelector((state: any) => {
    return state.asset.socketRealTimeUpdateData;
  }); */

  const selectedItems = useSelector((state: any) => state.table.selectedItems);

  const {
    data: floorDetails,
    isLoading: isLoadingFloorDetails,
    error: floorDetailsError,
  } = useQuery({
    queryKey: ["getFloorDetails", blockCode, floor, query],
    queryFn: ({ signal }: any) => getFloorDetails(blockCode, floor, signal),
    enabled: !!blockCode && !!floor,
    staleTime: 0, // data becomes stale immediately
    gcTime: 0, // optional: remove cached data asap (if not needed)
    refetchOnMount: "always", // always refetch when component mounts
    refetchOnReconnect: "always",
  });

  let {
    data: floorAssetsAndShapes,
    isLoading: isLoadingFloorAssetsAndShapes,
    refetch: refetchFloorAssetsAndShapes,
  } = useQuery({
    queryKey: ["getFloorAssetsAndShapes", blockCode, floor, query],
    queryFn: ({ signal }: any) =>
      getFloorAssetsAndShapes(blockCode, floor, signal),
    enabled: !!blockCode && !!floor,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
  });

  useEffect(() => {
    setInitialValuesFloor({
      type: "2",
    });

    setSelectedType("2");

    setUpdateJSX((updateJSX) => updateJSX + 1);
  }, [query]);

  /* useEffect(() => {
    const copiedSocketRealTimeUpdate = _.cloneDeep(socketRealTimeUpdate);

    if (
      copiedSocketRealTimeUpdate.LightStatusReading &&
      socketRealTimeUpdateData.BlockCode === blockCode &&
      socketRealTimeUpdateData.FloorCode === floorCode
    ) {
      const copiedFloorAssetsAndShapes = _.cloneDeep(floorAssetsAndShapes);
      const data = copiedFloorAssetsAndShapes?.data;

      if (!data?.length) return;

      const targetData = data.find(
        (asset: any) => asset.assetCode === socketRealTimeUpdateData.AssetCode
      );

      if (!targetData) return;

      targetData.isLightOn = socketRealTimeUpdateData.IsLightOnline;
      if (socketRealTimeUpdateData.IsLightOnline) {
        targetData.status = "Online";
      } else {
        targetData.status = "Offline";
      }

      dispatch(setFloorAssetsAndShapesData(copiedFloorAssetsAndShapes));
    }
  }, [socketRealTimeUpdate]); */

  const faults = useSelector((state: any) => {
    return state.fault.faults;
  });

  const isLoadingFaults = useSelector((state: any) => {
    return state.fault.isLoadingFaults;
  });

  const faultDetails = useSelector((state: any) => {
    return state.fault.faultDetails;
  });

  let isLoadingFaultDetails = useSelector((state: any) => {
    return state.fault.isLoadingFaultDetails;
  });

  useEffect(() => {
    setSelectedFile(null);
    setShapes([]);
  }, [blockCode, floor, query]);

  useEffect(() => {
    if (floorDetails?.data) {
      setIsProcessingImage(true);
      const base64file = floorDetails?.data?.floorImage;

      if (!base64file) {
        setIsProcessingImage(false);
        return;
      }
      // If imageFile is a base64 string, create a data URL
      const isBase64 =
        typeof base64file === "string" && base64file?.startsWith("iVBOR");
      const src = isBase64 ? `data:image/png;base64,${base64file}` : base64file;

      const imageElement = new window.Image();
      imageElement.src = src;
      imageElement.onload = () => setSelectedFile(imageElement);
      setTimeout(() => {
        setIsProcessingImage(false);
      }, 300);
    }

    if (floorDetailsError) {
      setIsProcessingImage(false);
    }
  }, [floorDetails, floorDetailsError]);

  useEffect(() => {
    if (isLoadingFloorDetails) {
      setIsProcessingImage(true);
    }
  }, [isLoadingFloorDetails]);

  useEffect(() => {
    isLoadingFloorAssetsAndShapes = true;

    let row = 0;
    let col = 0;
    const itemsPerRow = 43;
    const yStep = 20;
    const xStep = 20;

    const updatedShapes: FloorShape[] = floorAssetsAndShapes?.data?.map(
      (item: FloorShape) => {
        const x = item?.x ? item.x : col * xStep + xStep;

        const y = item?.y ? item.y : row * yStep;

        col++;

        if (col >= itemsPerRow) {
          col = 0;
          row++;
        }

        return {
          id: item?.assetCode,
          status: item?.status,
          x: x,
          y,
          assetCode: item?.assetCode,
          assetTypeId: item?.assetTypeId,
          dimLevel: item?.dimLevel,
          brightLevel: item?.brightLevel,
          floorLocation: item?.floorLocation,
          assetType: item?.assetType,
          faultType: item?.faultType,
          sensorDisplayName: item?.sensorDisplayName,
          labelDisplayName: item?.labelDisplayName,
          isLightOn: item?.isLightOn,
          lastOnline: item?.lastOnline,
          motionCount: item?.motionCount,
          displayLocationName: item?.displayLocationName,
          daylight: item?.daylight,
          lightType: item?.lightType,
          parentAssetTypeId: item?.parentAssetTypeId,
          highlightedAsset:
            item?.assetCode === selectedSearchedAssetCodeForHighlighting,
        };
      },
    );

    setShapes(updatedShapes);
    isLoadingFloorAssetsAndShapes = false;
  }, [floorAssetsAndShapes, selectedSearchedAssetCodeForHighlighting]);

  useEffect(() => {
    if (!faultDetails || !Object.keys(faultDetails).length) return;

    isLoadingFaultDetails = true;

    setInitialValues((prevValues: any) => ({
      ...prevValues,
      address: faultDetails?.address || "",
      sensorId: faultDetails?.assetCode || "",
      faultDateTime: faultDetails?.timestamp || "",
      gateway: faultDetails?.gateway || "",
      faultDescription: faultDetails?.faultDescription || "",
      remarks: faultDetails?.remarks || "",
    }));

    isLoadingFaultDetails = false;
  }, [faultDetails]);

  const handleChangePage = useCallback((page: number): void => {
    setPageNum(page);
  }, []);

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(sortBy);
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    [],
  );

  useEffect(() => {
    dispatch(
      getFaults({
        PageNum: PageNumber,
        pageSize: pageSize,
        tcCode: null,
        siteCode: null,
        blockCode: blockCode,
        floorCode: floorCode,
        SortBy,
        SortDirection,
        isAcknowledged: 5,
      }),
    );
  }, [PageNumber, SortBy, SortDirection, floor]);

  useEffect(() => {
    dispatch({
      type: "table/toggleSelectAll",
      payload: [],
    });

    dispatch({
      type: "table/toggleSelect",
      payload: { page: null, value: null, status: null },
    });
  }, []);

  const onRefetchData = () => {
    dispatch(
      getFaults({
        PageNum: PageNumber,
        pageSize: pageSize,
        tcCode: null,
        siteCode: null,
        blockCode: blockCode,
        floorCode: floorCode,
        SortBy,
        SortDirection,
        isAcknowledged: 5,
      }),
    ).then(() => {
      dispatch(
        GetAllFloorsByBlockCodeWithFaultCount({
          blockCode,
          PageNumber: 1,
          PageSize: 25,
        }),
      );
    });
  };

  const onSubmitModal = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const { remarks } = form.values;

      if (remarks?.length > 200) {
        return;
      }

      const finalItems = selectedItems?.filter(
        (item: any) => item?.value != null,
      );

      if (isAcknowledgeOpen) {
        const payload = {
          alarmReadingIds: finalItems?.map((item: any) => item?.value),
          remarks: remarks,
        };

        dispatch(acknowledgementFault({ payload })).then(() => {
          dispatch({
            type: "table/toggleSelectAll",
            payload: [],
          });

          dispatch({
            type: "table/toggleSelect",
            payload: { page: null, value: null, status: null },
          });

          handleCloseModal();
          onRefetchData();
        });
      } else if (isResolveOpen) {
        const payload = {
          alarmReadingIds: finalItems?.map((item: any) => item?.value),
          remarks: remarks,
        };

        dispatch(resolveFault({ payload })).then(() => {
          dispatch({
            type: "table/toggleSelectAll",
            payload: [],
          });

          dispatch({
            type: "table/toggleSelect",
            payload: { page: null, value: null, status: null },
          });

          handleCloseModal();
          onRefetchData();

          dispatch(setResolveStatusValue(Date.now()));
        });
      }
    }
  };

  const modalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitModal,
      variant: "primary",
    },
  ];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsAcknowledgeOpen(false);
    setIsResolveOpen(false);

    setInitialValues({
      blockNumber: "",
      sensorId: "",
      faultDateTime: "",
      gateway: "",
      zone: "",
      faultDescription: "",
      remarks: "",
    });
  };

  const [selectedType, setSelectedType] = useState("2");

  const onType = (value: any) => {
    setSelectedType(value);
    if (value === "2") {
      setUpdateJSX(updateJSX + 1);
    } else {
      setUpdateJSXHeatmap(updateJSXHeatmap + 1);
    }
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "remarks",
        label: "Remarks",
        placeholder: "Remarks",
        type: "textarea",
      },
    ],
  };

  const formConfigFloor: FormConfig = {
    controls: [
      {
        name: "type",
        label: "",
        type: "select",
        options: [
          {
            label: "Heatmap",
            value: "1",
          },
          {
            label: "Light Status",
            value: "2",
          },
        ],
        onChange: onType,
        disableFormFieldsLabel: true,
      },
    ],
  };

  const emptyConfig = {
    title: "No Fault Found",
    description: "",
  };

  const validationSchema = yup.object().shape({
    blockNumber: yup.string().required("Block Id is required"),
    sensorId: yup.string().required("Sensor Id is required"),
    faultDateTime: yup.string().required("Report Date & Time is required"),
    gateway: yup.string().required("Gateway is required"),
    zone: yup.string().required("Zone is required"),
    remarks: yup
      .string()
      .max(200, "Remarks must be at most 200 characters")
      .required("Remarks is required"),
  });

  const validationSchemaFloor = yup.object().shape({});

  const acknowledgeButtonAction = (id: any) => {
    setModalLabel("Fault Acknowledge");
    setIsModalOpen(true);
    setIsAcknowledgeOpen(true);
  };

  const resolveButtonAction = () => {
    setModalLabel("Fault Resolve");
    setIsModalOpen(true);
    setIsResolveOpen(true);
  };

  useEffect(() => {
    const isAcknowledgedExists = selectedItems?.some(
      (item: any) => item?.status === "Acknowledged",
    );

    if (selectedItems?.length > 1) {
      setIsResolveDisabled(false);
      setIsAcknowledgeDisabled(isAcknowledgedExists);
    } else {
      setIsResolveDisabled(true);
      setIsAcknowledgeDisabled(true);
    }
  }, [selectedItems]);

  // Responsive button colour state
  const [updateButtonColour, setupdateButtonColour] = useState(
    window.innerWidth > 1200 ? "default" : "primary",
  );

  // Responsive search bar length state
  // Function to determine initial search bar length based on window width
  const getInitialSearchBarLength = () => {
    if (window.innerWidth <= 768) return "200px";
    if (window.innerWidth <= 992) return "250px";
    if (window.innerWidth <= 1200) return "350px";
    return "450px";
  };
  const [searchBarLength, setSearchBarLength] = useState(
    getInitialSearchBarLength(),
  );

  useEffect(() => {
    const handleResize = () => {
      setupdateButtonColour(window.innerWidth > 1200 ? "default" : "primary");
      setSearchBarLength(getInitialSearchBarLength());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex flex-col">
      <>
        {!query && (
          <div className="d-flex flex-row justify-content-between gap-3 align-items-center">
            <div className="d-flex justify-content-start gap-3 align-items-center">
              <div className="d-flex justify-content-start gap-3 dashboard-generic-actions">
                <HasPermission
                  moduleName="Floor Plan Detail"
                  actionName="Update-Floor Plan Detail"
                  isRoute={false}
                >
                  <B2Button
                    variant={updateButtonColour}
                    className="d-block cursor-pointer"
                    onClick={() => {
                      navigate(`${pathname}?update=true`);
                    }}
                  >
                    <FontAwesomeIcon icon={faPencil} className="fs-7 me-2" />
                    Update Floor Plan
                  </B2Button>
                </HasPermission>
                <HasPermission
                  moduleName={DashboardModules.FLOOR_PLAN_DETAILS}
                  actionName="View-Floor Plan Detail"
                  isRoute={false}
                >
                  <B2Button
                    variant={updateButtonColour}
                    className="d-block cursor-pointer"
                    onClick={() => {
                      if (selectedSearchedAssetCodeForHighlighting) {
                        navigate(
                          `${pathname}/floor-analytics?assetCode=${btoa(
                            selectedSearchedAssetCodeForHighlighting,
                          )}`,
                        );
                      } else {
                        navigate(`${pathname}/floor-analytics`);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faChartLine} className="fs-7 me-2" />
                    Analytics View
                  </B2Button>
                </HasPermission>
              </div>
            </div>

            <div
              style={{
                position: "relative",
              }}
            >
              <DashboardFloorplanWildcardSearchingComponent
                setSelectedSearchedAssetCodeForHighlighting={
                  setSelectedSearchedAssetCodeForHighlighting
                }
                searchBarLength={searchBarLength}
              />
            </div>

            <div
              style={{
                position: "relative",
              }}
            >
              <FormBuilderComponent
                formRef={formRefFloor}
                initialValues={initialValuesFloor}
                validation={validationSchemaFloor}
                formConfig={formConfigFloor}
              />
            </div>
          </div>
        )}

        <div>
          <div className="mb-3">
            {query ? (
              <div>
                <UpdateFloorPlanComponent
                  refetchFloorAssetsAndShapes={refetchFloorAssetsAndShapes}
                />
              </div>
            ) : (
              <div>
                {selectedType === "2" ? (
                  <FloorPlanComponent
                    key={updateJSX}
                    imageFile={selectedFile}
                    responseShapes={shapes}
                    isLoadingFloorAssetsAndShapes={
                      isLoadingFloorAssetsAndShapes
                    }
                    isLoadingFloorDetails={isLoadingFloorDetails}
                    selectedSearchedAssetCodeForHighlighting={
                      selectedSearchedAssetCodeForHighlighting
                    }
                    setSelectedSearchedAssetCodeForHighlighting={
                      setSelectedSearchedAssetCodeForHighlighting
                    }
                    isProcessingImage={isProcessingImage}
                  />
                ) : (
                  <FloorPlanHeatmapComponent
                    key={updateJSXHeatmap}
                    imageFile={selectedFile}
                    responseShapes={shapes}
                    isLoading={isLoadingFloorAssetsAndShapes}
                    highlightedAsset={selectedSearchedAssetCodeForHighlighting}
                    setSelectedSearchedAssetCodeForHighlighting={
                      setSelectedSearchedAssetCodeForHighlighting
                    }
                    isProcessingImage={isProcessingImage}
                  />
                )}
              </div>
            )}
          </div>

          {!query && (
            <>
              <div className="d-flex justify-content-center mt-5 align-items-center">
                <div>
                  <img
                    src={circleLightOff}
                    alt="Luminaire Off"
                    width="20px"
                    height="20px"
                  />
                </div>
                <span className="ms-1 me-4">Luminaire</span>
                <div>
                  <img
                    src={gatewayInitial}
                    alt="Gateway"
                    width="25px"
                    height="30px"
                  />
                </div>
                <span className="ms-1 me-4">Gateway</span>
                <div>
                  <img
                    src={photocellOffline}
                    alt="Sensor"
                    width="22px"
                    height="22px"
                  />
                </div>
                <span className="ms-1 me-4">Sensor</span>
              </div>

              {selectedType === "2" && (
                <div
                  className="d-flex justify-content-center mt-3 gap-5 legend"
                  style={{ marginBottom: "1rem" }}
                >
                  <div className="d-flex gap-2">
                    <img
                      src={circleLightOn}
                      alt="Luminaire On"
                      width="18px"
                      height="18px"
                    />
                    <p>On</p>
                  </div>

                  <div className="d-flex gap-2">
                    <img
                      src={circleLightOff}
                      alt="Luminaire Off"
                      width="18px"
                      height="18px"
                    />
                    <p>Off</p>
                  </div>

                  <div className="d-flex gap-2">
                    <img
                      src={circleLightFault}
                      alt="Luminaire Fault"
                      width="18px"
                      height="18px"
                    />
                    <p>Fault</p>
                  </div>
                </div>
              )}

              {selectedType === "1" && (
                <div className="legend-container">
                  <div className="d-flex gap-2">
                    <img
                      src={motion0to25}
                      alt="0-25 Motions"
                      width="18px"
                      height="18px"
                    />
                    <p>0-25 Motions</p>
                  </div>

                  <div className="d-flex gap-2">
                    <img
                      src={motion26to50}
                      alt="26-50 Motions"
                      width="18px"
                      height="18px"
                    />
                    <p>26-50 Motions</p>
                  </div>

                  <div className="d-flex gap-2">
                    <img
                      src={motion51to75}
                      alt="51-75 Motions"
                      width="18px"
                      height="18px"
                    />
                    <p>51-75 Motions</p>
                  </div>

                  <div className="d-flex gap-2">
                    <img
                      src={motionMoreThan75}
                      alt="75+ Motions"
                      width="18px"
                      height="18px"
                    />
                    <p>75+ Motions</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </>

      <div className="mt-3">
        <div className="mt-4 mb-4">
          <TitleComponent title={`Reported Faults`} />
        </div>

        <div style={{ marginTop: "2rem" }}>
          <div className="my-3 ">
            <div className="d-flex justify-content-end table-generic-action">
              <HasPermission
                moduleName="Fault"
                actionName="Acknowledge-Fault"
                isRoute={false}
              >
                <div className="d-flex justify-end items-end">
                  <B2Button
                    variant="primary"
                    onClick={acknowledgeButtonAction}
                    disabled={isAcknowledgeDisabled}
                  >
                    Acknowledge
                  </B2Button>
                  <B2Button
                    variant="primary"
                    onClick={resolveButtonAction}
                    className="ms-2"
                    disabled={isResolveDisabled}
                  >
                    Resolve
                  </B2Button>
                </div>
              </HasPermission>
            </div>
            <div className="mt-3">
              <TableComponent
                tableHeaders={columns}
                data={faults?.data}
                totalNumberOfRecords={faults?.numberOfRecords}
                pageSize={pageSize}
                current={PageNumber}
                onChangeSort={handleChangeSort}
                onChangePage={handleChangePage}
                emptyConfig={emptyConfig}
                // actionConfig={actionConfig}
                isLoadingData={isLoadingFaults}
                enableSelection={true}
                isFaultTable={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={modalLabel}
          actionConfig={modalActionConfig}
        >
          <SkeletonComponent
            wrapperHeight="380px"
            skeletonHeight="50px"
            isLoading={isLoadingFaultDetails}
            count={5}
          >
            <FormBuilderComponent
              formRef={formRef}
              initialValues={initialValues}
              validation={validationSchema}
              formConfig={formConfig}
            />
          </SkeletonComponent>
        </ModalComponent>
      )}
    </div>
  );
};

export default DashboardFloorPlanViewComponent;
